import { CommonModule } from '@angular/common'
import { Component, computed, DestroyRef, inject, input, OnInit } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { RouterLink } from '@angular/router'
import { Uuid } from '@ftr/contracts/type/shared'
import { CourtSystemsState } from '@ftr/data-court-system'
import { NotificationModule, NotificationType, PageComponent, RouterActivityService, selectWith } from '@ftr/foundation'
import { AppPaths, CourtSystemAdminPaths, CourtSystemPaths } from '@ftr/routing-paths'
import { LaunchDarklyModule, LaunchDarklyService } from '@ftr/ui-feature-flags'
import { AuthenticationService, GetCurrentUserAction, IfGlobalAdminDirective } from '@ftr/ui-user'
import { Store } from '@ngxs/store'
import { debounceTime, distinctUntilChanged } from 'rxjs'
import { WindowRefService } from '../../services'

export const defaultForbiddenError =
  'Sorry, you do not have access to this page. Please use the navigation menu to select another page.'

const PROD_COURT_SYSTEMS_FTR_STAFF_CANT_ACCESS = [
  // Kansas 9th Judicial District
  '270b6f36-f4ab-45c6-b4bd-627be9ae2da3',
  // Trinidad & Tobago
  '06efb090-a7ab-4586-9d0b-842112124e54',
]

@Component({
  selector: 'ftr-forbidden',
  templateUrl: './forbidden.component.html',
  imports: [CommonModule, NotificationModule, PageComponent, LaunchDarklyModule, IfGlobalAdminDirective, RouterLink],
})
export class ForbiddenComponent implements OnInit {
  readonly courtSystemId = input<Uuid>()

  protected readonly courtSystem = selectWith(() => CourtSystemsState.courtSystemById(this.courtSystemId()!))

  protected readonly addUserRouterLink = computed(() =>
    this.courtSystemId()
      ? ['/', AppPaths.CourtSystem, this.courtSystemId()!, CourtSystemPaths.Admin, CourtSystemAdminPaths.Users, 'add']
      : null,
  )

  protected readonly isForbiddenCourtSystemForFtrStaff = computed(() =>
    PROD_COURT_SYSTEMS_FTR_STAFF_CANT_ACCESS.find(i => i === this.courtSystemId()) ? true : false,
  )

  readonly #destroyRef = inject(DestroyRef)

  constructor(
    private readonly routerActivityService: RouterActivityService,
    private readonly windowRefService: WindowRefService,
    private readonly launchDarklyService: LaunchDarklyService,
    private readonly authenticationService: AuthenticationService,
    private readonly store: Store,
  ) {}

  errorMessage: string

  readonly notificationType = NotificationType

  async ngOnInit(): Promise<void> {
    await this.routerActivityService.waitForRedirect()
    const state = this.windowRefService.history().state
    this.authenticationService.initialize().then(() => {
      if (!this.authenticationService.getCurrentUserId()) {
        this.errorMessage = state?.userMessage || defaultForbiddenError
      } else {
        this.store.dispatch(new GetCurrentUserAction())
        this.launchDarklyService
          .observeFlagChanges<boolean>('detailed-error-messages')
          .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.#destroyRef))
          .subscribe(detailedErrorMessageEnabled => {
            if (detailedErrorMessageEnabled) {
              this.errorMessage = state?.developerMessage || state?.userMessage || defaultForbiddenError
            } else {
              this.errorMessage = state?.userMessage || defaultForbiddenError
            }
          })
      }
    })
  }
}
